import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Flag, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { flagApi, ticketPath, fmtWhen, FLAG_STATUS_STYLES } from '../components/caseload-flags/flagUtils';

const ALL = 'all';

const Filter = ({ value, onChange, items, allLabel, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-64 bg-card" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{allLabel}</SelectItem>
      {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function CaseloadFlagTicketsReportPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(ALL);
  const [cm, setCm] = useState(ALL);
  const [data, setData] = useState(null);

  useEffect(() => {
    flagApi.all({ status: status === ALL ? undefined : status, case_manager_id: cm === ALL ? undefined : cm })
      .then(setData).catch((err) => { toast.error(err.response?.data?.detail || 'Could not load tickets'); setData({ flags: [], statuses: [], case_managers: [] }); });
  }, [status, cm]);

  return (
    <div className="space-y-6" data-testid="caseload-tickets-report-page">
      <div>
        <Link to="/reports" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-4 w-4" />Reports</Link>
        <h1 className="text-2xl font-bold flex items-center gap-2 mt-1"><Flag className="h-6 w-6" />Caseload Review Tickets</h1>
        <p className="text-sm text-muted-foreground">Every Caseload Review flagged for correction. Open tickets are listed first.</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Filter value={status} onChange={setStatus} allLabel="All statuses" items={(data?.statuses || []).map((s) => ({ id: s, name: s }))} testid="tickets-status-filter" />
        <Filter value={cm} onChange={setCm} allLabel="All Case Managers" items={data?.case_managers || []} testid="tickets-cm-filter" />
      </div>
      <Card className="card-shadow">
        <CardContent className="p-0">
          {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
            <Table>
              <TableHeader><TableRow><TableHead>Participant</TableHead><TableHead>Case Manager</TableHead><TableHead>Status</TableHead><TableHead>Date Flagged</TableHead><TableHead>Flagged By</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.flags.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="tickets-empty">No tickets match these filters</TableCell></TableRow>}
                {data.flags.map((f) => (
                  <TableRow key={f.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(ticketPath(f.id))} data-testid={`ticket-row-${f.id}`}>
                    <TableCell className="font-medium">{f.participant_name}</TableCell>
                    <TableCell>{f.case_manager_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className={FLAG_STATUS_STYLES[f.status]}>{f.status}</Badge></TableCell>
                    <TableCell>{fmtWhen(f.created_at)}</TableCell>
                    <TableCell>{f.flagged_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
